import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import * as logsService from '../../services/api/logs';
import { Log } from '../../entities/log';
import { Pagination } from '../../entities/pagination';
import FilterLogs from '../../entities/filter/logs';
import { fillFromParams } from '../../services/crud/filter';
import { SimpleObject } from '../../entities/simple-object';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import { strings } from '../../localization/strings';
import Heading1 from '../../components/common/headings/heading1';
import LogsFilter from './components/logs-filter';
import LogsTable from './components/logs-table';

function ReportsLogsPageComponent() {
  const [queryParams, setQueryParams] = useSearchParams();

  const initialState: FilterLogs = {
    page: 0,
    sort: '-id',
  };

  const types = {
    isNumeric: ['page'],
    isDate: ['created_at_from', 'created_at_to'],
  };

  const { token } = useAuth();
  const [params, setParams] = useState<FilterLogs>(fillFromParams<FilterLogs>(initialState, queryParams, types));
  const [pagination, setPagination] = useState<Pagination>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [logs, setLogs] = useState<Log[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const items = await logsService.list(token, params);

        setLogs(items.items);
        setPagination(items.pagination);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchData();
  }, [token, params]);

  const setFilterData = (filterData: FilterLogs) => {
    const query: SimpleObject = {};

    for (const [key, value] of Object.entries(filterData)) {
      if (typeof value !== 'undefined' && value !== null && value !== '') {
        query[key] = value;
      }
    }

    setParams({ ...filterData, page: 0 });
    setQueryParams({ ...query, page: 0 } as SimpleObject);
  };

  const updatePage = (currentPage: number) => {
    setParams({ ...params, page: currentPage });
    setQueryParams({ ...params, page: currentPage } as SimpleObject);
  };

  return (
    <div>
      <header className="mb-3">
        <div className="flex justify-between space-x-3">
          <Heading1>{strings.navigation.reports.logs}</Heading1>
        </div>
      </header>
      <main className="w-full">
        <div className="overflow-hidden rounded-lg border border-gray-200 sm:rounded-lg">
          {error && (
            <div className="mx-auto my-5 max-w-7xl px-4 sm:px-6 lg:px-8">
              <AlertPrimary type="danger" text={error} />
            </div>
          )}
          <LogsFilter onUpdate={setFilterData} filterState={params} />
          {isLoading ? (
            <CommonAnimatedLoader />
          ) : (
            <>
              {logs.length > 0 && pagination && <LogsTable logs={logs} pagination={pagination} onUpdate={updatePage} />}
              {logs.length === 0 && (
                <div className="mx-auto max-w-7xl p-4 text-xl sm:p-6 lg:p-8">{strings.search.noResults}</div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default ReportsLogsPageComponent;
